import { AlertCircleIcon, HashIcon, LockIcon } from 'lucide-react';

import { useCheckPermissions } from '@/modules/auth/hooks/useCheckPermissions';
import { permissions } from '@/modules/auth/permissions';
import { LoadMoreChannelsError } from '@/modules/slack-notification-channel/components/LoadMoreChannelsError';
import { useSelectableSlackChannelsQuery } from '@/modules/slack-notification-channel/queries/useSelectableSlackChannelsQuery';
import { useSlackNotificationChannelQuery } from '@/modules/slack-notification-channel/queries/useSlackNotificationChannelQuery';
import { Alert, AlertDescription } from '@/modules/ui/components/alert/Alert';
import { Button } from '@/modules/ui/components/button/Button';
import { Combobox } from '@/modules/ui/components/combobox/Combobox';

const DEFAULT_VALUE = '__default__';

interface JobSlackChannelFieldProps {
  value: string | null;
  currentName?: string | null;
  onChange: (channelId: string | null) => void;
}

/** Selects an existing channel for this Job; clearing it uses the tenant destination. */
export function JobSlackChannelField({
  value,
  currentName,
  onChange,
}: JobSlackChannelFieldProps) {
  const hasPermission = useCheckPermissions();
  const canRead = hasPermission([permissions.SlackNotificationChannel.Read]);
  const channelsQuery = useSelectableSlackChannelsQuery({ enabled: canRead });
  const tenantChannelQuery = useSlackNotificationChannelQuery({
    enabled: canRead,
  });

  if (!canRead) return null;

  const channels = [...channelsQuery.channels].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true }),
  );
  const tenantChannel = tenantChannelQuery.data;
  // Without a job channel, messages go to the tenant channel, else to the Finlay app DM.
  const defaultLabel = tenantChannelQuery.isLoading
    ? 'Loading channels…'
    : tenantChannel
      ? `Default: #${tenantChannel.channelName}`
      : 'Finlay app DM';
  const options = [
    { value: DEFAULT_VALUE, label: defaultLabel },
    ...(value && !channels.some((channel) => channel.id === value)
      ? [{ value, label: currentName ?? value }]
      : []),
    ...channels.map((channel) => ({
      value: channel.id,
      label: channel.name,
      icon: channel.isPrivate ? (
        <LockIcon className="size-4 opacity-60" />
      ) : (
        <HashIcon className="size-4 opacity-60" />
      ),
    })),
  ];

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Job notification channel</label>
      <p className="text-muted-foreground text-sm">
        Candidate introductions and scheduling replies for this job go here.
        Changes apply to future messages.
      </p>
      {channelsQuery.isError && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription className="flex items-center justify-between gap-3">
            Couldn&rsquo;t load Slack channels.
            <Button variant="outline" size="sm" onClick={channelsQuery.retry}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <Combobox
        aria-label="Job notification channel"
        value={value ?? DEFAULT_VALUE}
        options={options}
        disableSort
        disabled={channelsQuery.isPending || tenantChannelQuery.isLoading}
        placeholder={
          channelsQuery.isPending ? 'Loading channels…' : 'Select a channel'
        }
        searchPlaceholder="Search channels…"
        // Search only covers loaded pages; don't call a channel missing yet.
        emptyMessage={
          channelsQuery.isLoadingMore
            ? 'No match yet. Still loading channels…'
            : 'No channels found.'
        }
        className="w-full sm:max-w-sm"
        onChange={(selected) =>
          onChange(selected === DEFAULT_VALUE ? null : selected)
        }
      />
      {channelsQuery.hasLoadMoreError && (
        <LoadMoreChannelsError onRetry={channelsQuery.retry} />
      )}
    </div>
  );
}
